import {
  InvestmentCategoryName,
  InvestmentGeographyName,
  InvestmentStageName,
  UsState
} from 'generated/graphql';
import * as yup from 'yup';



export const emailSchema = yup.string().email('Not a valid email.');
export const passwordSchema = yup.string().min(8, 'Must be at least 8 characters');
export const dateSchema = yup.date();

export const confirmPasswordSchema = (name: string) => yup.string()
  .test(
    'passwords-match',
    'Passwords must match',
    function (value?: string) {
      return this.parent[name] === value;
    }
  ).min(8, 'Must be at least 8 characters');

export const cardNumberSchema = yup
  .string()
  .test(
    'card-test',
    'Invalid Card Number',
    async value => !!value
  );

export const zipCodeSchema = yup
  .number()
  .min(4, 'Must be at least 4 digits')
  .max(5, 'Must be no longer than 5 digits');

const REGEXP_EXPIRATION_DATE = /^(0[1-9]|1[0-2])\/?([0-9]{4}|[0-9]{2})$/;

export const cardExpirationDateSchema = yup
  .string()
  .matches(REGEXP_EXPIRATION_DATE, {
    message: 'Incorrect expiration date',
    excludeEmptyString: false
  });

export const cardVerificationValue = yup
  .string()
  .min(3, 'Must be at least 3 numbers')
  .max(4, 'Must be no longer than 4 numbers');

export const codeSchema = yup.string()
  .min(6, 'Code must be no less than 6 characters')
  .max(6, 'Code must be no more than 6 characters')
  .required('Code is a required field.');

export const nonEmptyStringSchema = yup
  .string()
  .min(1, 'Must be at least one character.')
  .max(1000, 'Must be no longer than 1000 characters.');

export const nonEmptyStringArraySchema = yup
  .array()
  .of(yup.string().required())
  .min(1, 'Must be select at least one item.');

export const websiteUrlSchema = yup
  .string()
  .matches(
    /^((https?|ftp):\/\/)?(www.)?(((([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!$&'()*+,;=]|:)*@)?(((\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5])\.(\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5])\.(\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5])\.(\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5]))|((([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.)+(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.?)(:\d*)?)(\/((([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!$&'()*+,;=]|:|@)+(\/(([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!$&'()*+,;=]|:|@)*)*)?)?(\?((([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!$&'()*+,;=]|:|@)|[\uE000-\uF8FF]|\/|\?)*)?(#((([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!$&'()*+,;=]|:|@)|\/|\?)*)?$/i,
    {
      message: 'Must be a valid URL',
      excludeEmptyString: true
    }
  );

export const positiveIntegerSchema = {
  required: () => yup.number()
    .integer('Number must be integer')
    .min(0, 'Number must be non-negative'),
  nullable: () => positiveIntegerSchema
    .required()
    .nullable()
    .transform((_, val) => parseFloat(val) === Number(val) ? parseFloat(val) : null)
};

export const fileData = yup.object({
  name: yup.string().required(),
  dataUrl: yup.string().required(),
  file: yup.mixed().required(),
  fileSizeBytes: yup.number().required()
});



export const usStateSchema = yup.string().oneOf<UsState['displayName']>([
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO',
  'CT', 'DE', 'DC', 'FL', 'GA', 'HI',
  'ID', 'IL', 'IN', 'IA', 'KS', 'KY',
  'LA', 'ME', 'MD', 'MA', 'MI', 'MN',
  'MS', 'MO', 'MT', 'NE', 'NV', 'NH',
  'NJ', 'NM', 'NY', 'NC', 'ND', 'OH',
  'OK', 'OR', 'PA', 'RI', 'SC', 'SD',
  'TN', 'TX', 'UT', 'VT', 'VA', 'WA',
  'WV', 'WI', 'WY'
]);



export const investmentGeographySchema = yup.mixed<InvestmentGeographyName>().oneOf([
  'africa', 'agnostic', 'asia',
  'australiaNZ', 'bayAreaUS', 'canada',
  'china', 'europe', 'india',
  'ireland', 'israel', 'japan',
  'mexico', 'middleEast', 'midwestUS',
  'newYorkCityUS', 'northAmerica', 'northeastUS',
  'northwestUS', 'singapore', 'southAmerica',
  'southeastUS', 'southwestUS', 'uk',
  'us'
]);



export const investmentStageSchema = yup.mixed<InvestmentStageName>().oneOf([
  'growth',
  'preSeed',
  'seed',
  'venture'
]);



export const investmentCategorySchema = yup.mixed<InvestmentCategoryName>().oneOf([
  'acceleratorIncubator',
  'angelGroup',
  'corporateVentureCapital',
  'startups',
  'ventureCapital'
]);
